import { ConditionTypeEnum } from "../domain/ConditionType.enum";

interface TranslationConfig {
  entityProperty?: string;
  fieldName?: string;
}

interface TranslationEntry {
  locale: string;
  name?: string;
  text?: string;
  html?: string;
  [key: string]: unknown;
}

interface ConditionFormValues {
  id?: string | number;
  ticker?: string;
  earlyCharge?: string | number;
  minimumCharge?: string | number;
  conditionType?: boolean;
  translations?: TranslationEntry[];
  payments?: string[];
  propertyTicker?: string;
  _isChain?: boolean;
  _chainChildren?: string[];
  _isAgrInIndividual?: boolean;
  selectedMotors?: string[];
  selectedMotorsSection?: unknown;
  [key: string]: unknown;
}

interface NormalizedEntity {
  id?: string | number;
  ticker?: string;
  code?: string;
  name?: string;
  type?: string;
  earlyCharge: number;
  minimumCharge: number;
  conditionType: ConditionTypeEnum;
  translations?: TranslationEntry[];
  payments?: string[];
  propertyTicker?: string;
  [key: string]: unknown;
}

interface ChainBodyEntry {
  ticker?: string;
  propertyTicker: string;
  parentPropertyTicker?: string;
  parentTicker?: string;
  payments: string[];
  [key: string]: unknown;
}

export function normalizeConditionEntity(
  formValues: ConditionFormValues,
  translationConfig?: TranslationConfig
): NormalizedEntity {
  const {
    _isChain: _ic,
    _chainChildren: _cc,
    _isAgrInIndividual: _iai,
    selectedMotors: _sm,
    selectedMotorsSection: _sms,
    ...otherFormValues
  } = formValues;

  const entity: Record<string, unknown> = { ...otherFormValues };

  if (!entity.id) {
    delete entity.id;
  }

  if (translationConfig?.entityProperty && translationConfig.fieldName) {
    delete entity[translationConfig.fieldName];
    const translationValues = (formValues[translationConfig.fieldName] as TranslationEntry[]) || [];
    entity[translationConfig.entityProperty] = translationValues.map(t => ({ ...t }));
  }

  entity.code = formValues.ticker;
  entity.earlyCharge = parseFloat(String(formValues.earlyCharge)) || 0;
  entity.minimumCharge = parseFloat(String(formValues.minimumCharge)) || 0;
  entity.conditionType = formValues.conditionType
    ? ConditionTypeEnum.STORE
    : ConditionTypeEnum.ENGINE;

  if (formValues.translations) {
    formValues.translations.forEach((t: TranslationEntry, index: number) => {
      const translations = entity.translations as TranslationEntry[] | undefined;
      if (translations?.[index]) {
        translations[index].text = "text";
        translations[index].html = "html";
      }
    });
    entity.name = formValues.translations[0]?.name;
    entity.type = entity.name;
  }

  // The translationsList marker ({ translationsList: true }) creates an "undefined" key
  delete entity["undefined"];

  return (entity as unknown) as NormalizedEntity;
}

/**
 * Maarlab creates and confirms every Flight + Hotel reservation with this payment ticker
 * (PaymentType.Type.WITHOUT_CARD in witbooking-api), so it must exist on the engine.
 */
export const WITHOUT_CARD_PAYMENT_TICKER = "without_card";

/** Hidden while the V+H switch is on: Maarlab collects the money, so their values are fixed on submit. */
export const FLIGHT_HOTEL_FIXED_FIELDS = [
  "payments",
  "earlyCharge",
  "minimumCharge",
  "chargeFirstNight",
];

/** Overwrites the charge fields of a FLIGHT_HOTEL condition with the only values Maarlab supports. */
export function applyFlightHotelFixedValues(entity: NormalizedEntity): NormalizedEntity {
  return {
    ...entity,
    payments: [WITHOUT_CARD_PAYMENT_TICKER],
    earlyCharge: 100,
    minimumCharge: 0,
    chargeFirstNight: false,
  };
}

/** Fails closed: an unloaded or malformed payment type list counts as "no without_card". */
export function hasWithoutCardPaymentType(paymentTypes: unknown): boolean {
  return (
    Array.isArray(paymentTypes) &&
    paymentTypes.some(paymentType => paymentType?.ticker === WITHOUT_CARD_PAYMENT_TICKER)
  );
}

export function buildChainBody(
  conditionEntity: NormalizedEntity,
  propertyTicker: string,
  selectedMotors: string[]
): Record<string, ChainBodyEntry> {
  const ticker = conditionEntity.ticker;
  const payments = Array.isArray(conditionEntity.payments) ? [...conditionEntity.payments] : [];
  const hasReservationRequest = payments.some(p => p.toLowerCase() === "reservation_request");
  if (!hasReservationRequest) {
    payments.push("reservation_request");
  }

  const conditionBase: ChainBodyEntry = {
    ...conditionEntity,
    propertyTicker,
    payments,
  };

  const chainBody: Record<string, ChainBodyEntry> = {
    [propertyTicker]: { ...conditionBase, ticker },
  };

  selectedMotors.forEach(motorTicker => {
    chainBody[motorTicker] = {
      ...conditionBase,
      propertyTicker: motorTicker,
      parentPropertyTicker: propertyTicker,
      parentTicker: ticker,
    };
  });

  return chainBody;
}

export function buildAgrReadonlyLink(
  parentPropertyTicker: string,
  conditionTicker: string,
  entityName: string
): string {
  return `/v6/ng/${parentPropertyTicker}/${entityName}/${conditionTicker}`;
}
